//frontend/src/pages/company/Shops.jsx
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import CompanyLayout from '../../components/layout/CompanyLayout';
import { shopAPI, warehouseAPI, companyBankAccountAPI } from '../../services/api';

const Shops = () => {
  const { user, hasPermission } = useAuth();
  const [shops, setShops] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [currentCredentials, setCurrentCredentials] = useState(null);
  const [editingShop, setEditingShop] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
  const [formData, setFormData] = useState({
    shop_name: '',
    warehouse_id: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    pickup_address: '',
    delivery_fee: 0,
    status: 'active',
    create_user: false,
    company_bank_account_id: ''
  });

  useEffect(() => {
    fetchShops();
  }, [searchTerm, warehouseFilter, statusFilter, pagination.page]);

  useEffect(() => {
    fetchWarehouses();
    fetchBankAccounts();
  }, []);

  const fetchShops = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      if (searchTerm) params.search = searchTerm;
      if (warehouseFilter) params.warehouse_id = warehouseFilter;
      if (statusFilter) params.status = statusFilter;
      
      const response = await shopAPI.getAll(params);
      setShops(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Fetch shops error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const response = await warehouseAPI.getAll();
      setWarehouses(response.data.data || []);
    } catch (error) {
      console.error('Fetch warehouses error:', error);
      setWarehouses([]);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await companyBankAccountAPI.getAll();
      if (response.data.success) {
        setBankAccounts(response.data.data || []);
      }
    } catch (error) {
      console.error('Fetch bank accounts error:', error);
      setBankAccounts([]);
    }
  };

  const handleCreate = () => {
    setEditingShop(null);
    setFormData({
      shop_name: '',
      warehouse_id: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      pickup_address: '',
      delivery_fee: 0,
      status: 'active',
      create_user: false,
      company_bank_account_id: ''
    });
    setShowModal(true);
  };

  const handleEdit = (shop) => {
    setEditingShop(shop);
    setFormData({
      shop_name: shop.shop_name,
      warehouse_id: shop.warehouse_id,
      contact_person: shop.contact_person || '',
      phone: shop.phone,
      email: shop.email || '',
      address: shop.address || '',
      pickup_address: shop.pickup_address || '',
      delivery_fee: shop.default_delivery_fee || 0,
      status: shop.status,
      create_user: false,
      company_bank_account_id: shop.company_bank_account_id || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (submitting) return;
    
    try {
      setSubmitting(true);
      const submitData = {
        ...formData,
        delivery_fee: parseFloat(formData.delivery_fee) || 0
      };

      if (editingShop) {
        await shopAPI.update(editingShop.id, submitData);
        alert('Шоп амжилттай шинэчлэгдлээ');
        setShowModal(false);
      } else {
        const response = await shopAPI.create(submitData);
        
        if (response.data.data.credentials) {
          setCurrentCredentials(response.data.data.credentials);
          setShowCredentialsModal(true);
          setShowModal(false);
        } else {
          alert('Шоп амжилттай үүслээ');
          setShowModal(false);
        }
      }
      
      fetchShops();
    } catch (error) {
      alert(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Энэ шопыг устгахдаа итгэлтэй байна уу?')) return;
    try {
      await shopAPI.delete(id);
      alert('Шоп амжилттай устлаа');
      fetchShops();
    } catch (error) {
      alert(error.response?.data?.message || 'Устгахад алдаа гарлаа');
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      await shopAPI.toggleStatus(id, newStatus);
      fetchShops();
    } catch (error) {
      alert(error.response?.data?.message || 'Статус өөрчлөхөд алдаа гарлаа');
    }
  };

  const handleResetPassword = async (id) => {
    if (!confirm('Shop user-ийн нууц үгийг шинэчлэх үү?')) return;
    try {
      const response = await shopAPI.resetPassword(id);
      if (response.data.data.credentials) {
        setCurrentCredentials(response.data.data.credentials);
        setShowCredentialsModal(true);
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Нууц үг шинэчлэхэд алдаа гарлаа');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Хуулагдлаа!');
  };

  const getWarehouseName = (warehouseId) => {
    const warehouse = warehouses.find(w => w.id === warehouseId);
    return warehouse ? warehouse.warehouse_name : '';
  };

  const handlePageChange = (newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('mn-MN', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    });
  };

  if (loading && shops.length === 0) {
    return (
      <CompanyLayout>
        <div className="flex items-center justify-center h-64">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <div className="space-y-6">
        {/* HEADER */}
        <div className="flex items-center justify-between">
          <div></div>
          {hasPermission('shop.create') && (
            <button 
              onClick={handleCreate} 
              className="btn btn-primary flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Шоп нэмэх</span>
            </button>
          )}
        </div>

        {/* FILTERS */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <input 
                type="text" 
                placeholder="Шопын нэрээр хайх..." 
                value={searchTerm} 
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPagination({ ...pagination, page: 1 });
                }} 
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" 
              />
              <svg className="absolute left-3 top-3 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            
            <select 
              value={warehouseFilter} 
              onChange={(e) => {
                setWarehouseFilter(e.target.value);
                setPagination({ ...pagination, page: 1 });
              }} 
              className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Бүх агуулах</option>
              {warehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.warehouse_name}
                </option>
              ))}
            </select>
            
            <select 
              value={statusFilter} 
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination({ ...pagination, page: 1 });
              }} 
              className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Бүх статус</option>
              <option value="active">Идэвхтэй</option>
              <option value="inactive">Идэвхгүй</option>
            </select>
          </div>
        </div>

        {/* TABLE */}
        {shops.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <div className="text-6xl mb-4">🛍️</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Шоп байхгүй байна</h3>
            <p className="text-gray-600 mb-6">Эхний шопоо нэмээд эхлээрэй</p>
            {hasPermission('shop.create') && (
              <button onClick={handleCreate} className="btn btn-primary">+ Шоп нэмэх</button>
            )}
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        №
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Шоп
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Агуулах
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Холбоо барих
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Хүргэлт
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Бүртгэл
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Статус
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Үйлдэл
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {shops.map((shop, index) => (
                      <tr key={shop.id} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {(pagination.page - 1) * pagination.limit + index + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                              <span className="text-xl">🛍️</span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {shop.shop_name}
                              </div>
                              {shop.contact_person && (
                                <div className="text-sm text-gray-500">
                                  {shop.contact_person}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {shop.warehouse?.warehouse_name || getWarehouseName(shop.warehouse_id)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{shop.phone}</div>
                          {shop.email && (
                            <div className="text-sm text-gray-500">{shop.email}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {shop.default_delivery_fee > 0 ? `${Number(shop.default_delivery_fee).toLocaleString()}₮` : 'Үнэгүй'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {formatDate(shop.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            shop.status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {shop.status === 'active' ? 'Идэвхтэй' : 'Идэвхгүй'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            {/* Засах */}
                            {hasPermission('shop.edit') && (
                              <button
                                onClick={() => handleEdit(shop)}
                                className="text-blue-600 hover:text-blue-900 p-1.5 hover:bg-blue-50 rounded-lg transition"
                                title="Засах"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                            )}
                            
                            {/* Нууц үг шинэчлэх */}
                            {hasPermission('shop.edit') && (
                              <button
                                onClick={() => handleResetPassword(shop.id)}
                                className="text-orange-600 hover:text-orange-900 p-1.5 hover:bg-orange-50 rounded-lg transition"
                                title="Нууц үг шинэчлэх"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                </svg>
                              </button>
                            )}
                            
                            {/* Статус өөрчлөх */}
                            {hasPermission('shop.edit') && (
                              <button
                                onClick={() => handleToggleStatus(shop.id, shop.status)}
                                className={`p-1.5 rounded-lg transition ${
                                  shop.status === 'active'
                                    ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                    : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                                }`}
                                title={shop.status === 'active' ? 'Хаах' : 'Нээх'}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                              </button>
                            )}
                            
                            {/* Устгах */}
                            {hasPermission('shop.delete') && (
                              <button
                                onClick={() => handleDelete(shop.id)}
                                className="text-red-600 hover:text-red-900 p-1.5 hover:bg-red-50 rounded-lg transition"
                                title="Устгах"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* PAGINATION */}
            {pagination.pages > 1 && (
              <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Нийт <span className="font-medium">{pagination.total}</span> бүртгэл, 
                    <span className="font-medium"> {pagination.page}</span> / <span className="font-medium">{pagination.pages}</span> хуудас
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Өмнөх
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.pages}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Дараах
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* CREATE/EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span className="text-3xl">🛍️</span>
              <span>{editingShop ? 'Шоп засах' : 'Шоп нэмэх'}</span>
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* ROW 1 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Шопын нэр *
                  </label>
                  <input 
                    type="text" 
                    value={formData.shop_name} 
                    onChange={(e) => setFormData({ ...formData, shop_name: e.target.value })} 
                    className="admin-input" 
                    required 
                    placeholder="Жнь: Fashion Shop" 
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Агуулах *
                  </label>
                  <select 
                    value={formData.warehouse_id} 
                    onChange={(e) => setFormData({ ...formData, warehouse_id: e.target.value })} 
                    className="admin-input"
                    required
                  >
                    <option value="">-- Агуулах сонгох --</option>
                    {warehouses.map((warehouse) => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.warehouse_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* ROW 2 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Харилцагч
                  </label>
                  <input 
                    type="text" 
                    value={formData.contact_person} 
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} 
                    className="admin-input" 
                    placeholder="Нэр" 
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Утас *
                  </label>
                  <input 
                    type="tel" 
                    value={formData.phone} 
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })} 
                    className="admin-input" 
                    required 
                    placeholder="99112233" 
                  />
                </div>
              </div>

              {/* ROW 3 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    И-мэйл *
                  </label>
                  <input 
                    type="email" 
                    value={formData.email} 
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                    className="admin-input" 
                    required
                    placeholder="shop@example.com" 
                  />
                  <p className="text-xs text-gray-500 mt-1">Давхардахгүй байх ёстой</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Хүргэлтийн төлбөр (₮)
                  </label>
                  <input 
                    type="number" 
                    value={formData.delivery_fee} 
                    onChange={(e) => setFormData({ ...formData, delivery_fee: e.target.value })} 
                    className="admin-input" 
                    placeholder="0" 
                  />
                </div>
              </div>

              {/* ADDRESS */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Хаяг
                </label>
                <textarea 
                  value={formData.address} 
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })} 
                  className="admin-input" 
                  rows="2" 
                  placeholder="Дэлгэрэнгүй хаяг"
                ></textarea>
              </div>

              {/* PICKUP ADDRESS */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Авах хаяг
                </label>
                <textarea 
                  value={formData.pickup_address} 
                  onChange={(e) => setFormData({ ...formData, pickup_address: e.target.value })} 
                  className="admin-input" 
                  rows="2" 
                  placeholder="Бараа авах хаяг (хэрэв өөр бол)"
                ></textarea>
              </div>

              {/* BANK ACCOUNT */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  COD шилжүүлэх данс
                </label>
                <select 
                  value={formData.company_bank_account_id} 
                  onChange={(e) => setFormData({ ...formData, company_bank_account_id: e.target.value })} 
                  className="admin-input"
                >
                  <option value="">-- Данс сонгох --</option>
                  {bankAccounts.filter(acc => acc.is_active).map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.bank_name} - {account.account_number} ({account.account_name})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  COD мөнгийг энэ данс руу шилжүүлнэ
                </p>
                {bankAccounts.length === 0 && (
                  <p className="text-xs text-orange-600 mt-1">
                    ⚠️ Эхлээд Тохиргоо → Дансны тохиргоо хэсэгт данс нэмнэ үү
                  </p>
                )}
              </div>

              {/* STATUS */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Статус</label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input 
                      type="radio" 
                      name="status" 
                      value="active" 
                      checked={formData.status === 'active'} 
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })} 
                      className="mr-2" 
                    />
                    <span className="text-sm">Идэвхтэй</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input 
                      type="radio" 
                      name="status" 
                      value="inactive" 
                      checked={formData.status === 'inactive'} 
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })} 
                      className="mr-2" 
                    />
                    <span className="text-sm">Идэвхгүй</span>
                  </label>
                </div>
              </div>

              {/* CREATE USER CHECKBOX (only for new shops) */}
              {!editingShop && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <label className="flex items-start cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={formData.create_user} 
                      onChange={(e) => setFormData({ ...formData, create_user: e.target.checked })} 
                      className="mt-1 mr-3" 
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">Shop user үүсгэх</span>
                      <p className="text-xs text-gray-600 mt-1">
                        Автоматаар username (жнь: shop247), password үүсгэж өгнө.
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {/* BUTTONS */}
              <div className="flex items-center gap-3 pt-4">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition"
                  disabled={submitting}
                >
                  Болих
                </button>
                <button 
                  type="submit" 
                  className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={submitting}
                >
                  {submitting ? '⏳ Уншиж байна...' : '✅ Хадгалах'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CREDENTIALS MODAL */}
      {showCredentialsModal && currentCredentials && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="text-3xl">🔑</span>
              <span>Shop User Мэдээлэл</span>
            </h2>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-yellow-800">
                ⚠️ {currentCredentials.message}
              </p>
            </div>

            <div className="space-y-4">
              {/* USERNAME */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Нэвтрэх нэр
                </label>
                <div className="flex items-center gap-2">
                  <input 
                    type="text" 
                    value={currentCredentials.username} 
                    readOnly
                    className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(currentCredentials.username)}
                    className="px-4 py-2.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
                    title="Хуулах"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* PASSWORD */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Нууц үг
                </label>
                <div className="flex items-center gap-2">
                  <input 
                    type="text" 
                    value={currentCredentials.password} 
                    readOnly
                    className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(currentCredentials.password)}
                    className="px-4 py-2.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
                    title="Хуулах"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* COPY BOTH */}
              <button
                onClick={() => copyToClipboard(`Username: ${currentCredentials.username}\nPassword: ${currentCredentials.password}`)}
                className="w-full px-4 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span>Бүгдийг хуулах</span>
              </button>
            </div>

            <button
              onClick={() => setShowCredentialsModal(false)}
              className="w-full mt-4 px-4 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition"
            >
              Хаах
            </button>
          </div>
        </div>
      )}
    </CompanyLayout>
  );
};

export default Shops;